import HelpOutline from '@mui/icons-material/HelpOutline'
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { LineItem } from '@/hooks/useDocumentInvoiceData'
import { getMaxDecimals } from '@/utils/decimal'
import {
  CellColumn,
  CellDiscrepancy,
  emphasizedCellKey,
} from '@/utils/po-discrepancies'
import DiscrepancyNumberCell from './DiscrepancyNumberCell'
import { JobNumberCell, PhaseNumberCell } from './NumberCell'
import { formatValue, hidesLumpSumRate } from './shared'

const REMAINING_TOOLTIP_MESSAGE =
  "Remaining reflects only what's currently available in the ERP and doesn't account for other invoices being matched against this PO right now."

type RemainingColumnHeadingProps = { label: string }

function RemainingColumnHeading({ label }: RemainingColumnHeadingProps) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent="flex-end"
    >
      <span>{label}</span>
      <Tooltip title={REMAINING_TOOLTIP_MESSAGE} enterDelay={200} arrow>
        <HelpOutline sx={{ color: 'text.secondary', fontSize: '14px' }} />
      </Tooltip>
    </Stack>
  )
}

type RemainingOrderedValueProps = {
  remaining: string | null | undefined
  ordered: string | null
  decimals?: number
  prefix?: string
}

function RemainingOrderedValue({
  remaining,
  ordered,
  decimals,
  prefix = '',
}: RemainingOrderedValueProps) {
  return (
    <>
      {prefix}
      {formatValue(remaining ?? null, { decimals })}
      {' / '}
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {formatValue(ordered, { decimals })}
      </Box>
    </>
  )
}

export type LineItemsTableRow = LineItem & {
  remainingQuantity?: string | null
  remainingAmount?: string | null
  /**
   * This table is used for both PO lines and invoice lines. Only the PO side sets this field,
   * using `isLumpSumPoLine`. On the invoice side the same fields hold invoice figures instead,
   * and an invoice line billing no units means something different, so it is left unset there.
   */
  isLumpSum?: boolean
}

export type CellDiscrepanciesMap = Map<
  string,
  Partial<Record<CellColumn, CellDiscrepancy>>
>

/** Which columns to render — a column is hidden (not just emptied) when the project has no matching field configured, or (for job/phase) when it's folded into the header instead. */
export type LineItemColumnVisibility = {
  description: boolean
  job: boolean
  phase: boolean
  quantity: boolean
  rate: boolean
  amount: boolean
}

const ALL_COLUMNS_VISIBLE: LineItemColumnVisibility = {
  description: true,
  job: true,
  phase: true,
  quantity: true,
  rate: true,
  amount: true,
}

type LineItemsTableProps = {
  rows: LineItemsTableRow[]
  rowRefCallback?: (rowId: string) => (el: HTMLTableRowElement | null) => void
  highlightedRowIds?: Set<string>
  columnVisibility?: LineItemColumnVisibility
  /** Per-row cell discrepancies, keyed by row id then column. */
  cellDiscrepancies?: CellDiscrepanciesMap
  /** Rows rendered dimmed (e.g. invoice rows with no PO match). */
  dimmedRowIds?: Set<string>
  /** Cells with a discrepancy to render with the emphasized (open-popover) treatment, keyed via `emphasizedCellKey`. */
  emphasizedCells?: Set<string>
  onRowHover?: (rowId: string | null) => void
  /** Fired when a cell with a discrepancy is clicked, with the clicked cell element as the popover anchor. */
  onDiscrepancyCellClick?: (
    rowId: string,
    column: CellColumn,
    anchorEl: HTMLElement,
  ) => void
  /** Fired when the pointer enters (column set) or leaves (column null) a cell with a discrepancy, so the parent can emphasize the whole match group. */
  onDiscrepancyCellHover?: (rowId: string, column: CellColumn | null) => void
}

export default function LineItemsTable({
  rows,
  rowRefCallback,
  highlightedRowIds,
  columnVisibility = ALL_COLUMNS_VISIBLE,
  cellDiscrepancies,
  dimmedRowIds,
  emphasizedCells,
  onRowHover,
  onDiscrepancyCellClick,
  onDiscrepancyCellHover,
}: LineItemsTableProps) {
  const hasRemainingQuantity = rows.some(
    (row) => row.remainingQuantity !== undefined,
  )
  const hasRemainingAmount = rows.some(
    (row) => row.remainingAmount !== undefined,
  )

  const qtyDecimals = getMaxDecimals(rows.map((row) => row.quantity))
  const rateDecimals = Math.max(2, getMaxDecimals(rows.map((row) => row.rate)))
  const amountDecimals = Math.max(
    2,
    getMaxDecimals(rows.map((row) => row.amount)),
  )

  return (
    <>
      {rows.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 1, fontStyle: 'italic' }}
        >
          No line items
        </Typography>
      ) : (
        // Bleed to the card edges (past CardContent's p: 2) so row highlight
        // backgrounds span the full card width; edge cells carry the padding.
        <TableContainer
          sx={{
            width: (theme) => `calc(100% + ${theme.spacing(4)})`,
            mx: -2,
          }}
        >
          <Table
            size="small"
            sx={{
              // Edge padding follows whichever column ends up first/last —
              // columns can be hidden when the project doesn't configure that field.
              '& tr > *:first-of-type': { pl: 2 },
              '& tr > *:last-of-type': { pr: 2 },
            }}
          >
            <TableHead>
              <TableRow>
                {columnVisibility.description && (
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                )}
                {columnVisibility.job && (
                  <TableCell sx={{ fontWeight: 600 }}>Job</TableCell>
                )}
                {columnVisibility.phase && (
                  <TableCell sx={{ fontWeight: 600 }}>Phase</TableCell>
                )}
                {columnVisibility.quantity && (
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    {hasRemainingQuantity ? (
                      <RemainingColumnHeading label="Quantity" />
                    ) : (
                      'Quantity'
                    )}
                  </TableCell>
                )}
                {columnVisibility.rate && (
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Rate
                  </TableCell>
                )}
                {columnVisibility.amount && (
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    {hasRemainingAmount ? (
                      <RemainingColumnHeading label="Amount" />
                    ) : (
                      'Amount'
                    )}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const discrepancies = cellDiscrepancies?.get(row.rowId)
                const quantityDiscrepancy = discrepancies?.quantity
                const rateDiscrepancy = discrepancies?.rate
                const amountDiscrepancy = discrepancies?.amount

                const isEmphasized = (column: CellColumn) =>
                  !!emphasizedCells?.has(emphasizedCellKey(row.rowId, column))
                const clickHandler =
                  (column: CellColumn) => (anchorEl: HTMLElement) =>
                    onDiscrepancyCellClick?.(row.rowId, column, anchorEl)
                const hoverChange =
                  (column: CellColumn) => (hovering: boolean) =>
                    onDiscrepancyCellHover?.(
                      row.rowId,
                      hovering ? column : null,
                    )

                return (
                  <TableRow
                    key={row.rowId}
                    ref={rowRefCallback?.(row.rowId)}
                    data-dimmed={
                      dimmedRowIds?.has(row.rowId) ? 'true' : undefined
                    }
                    onMouseEnter={() => onRowHover?.(row.rowId)}
                    onMouseLeave={() => onRowHover?.(null)}
                    sx={{
                      '& td': { borderBottom: 0, verticalAlign: 'middle' },
                      ...(highlightedRowIds?.has(row.rowId) && {
                        bgcolor: 'action.selected',
                      }),
                      // Dim rows with no PO match so they read as unmatched.
                      ...(dimmedRowIds?.has(row.rowId) && { opacity: 0.5 }),
                      transition:
                        'background-color 180ms ease, opacity 180ms ease',
                    }}
                  >
                    {columnVisibility.description && (
                      <TableCell sx={{ py: 0.5, maxWidth: 200 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ lineHeight: 'inherit' }}
                          title={row.description ?? undefined}
                        >
                          {row.description ?? '—'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.job && (
                      <JobNumberCell
                        num={row.jobNumber}
                        displayName={row.jobDisplayName}
                      />
                    )}
                    {columnVisibility.phase && (
                      <PhaseNumberCell
                        num={row.phaseNumber}
                        displayName={row.phaseDisplayName}
                        jobNumber={row.jobNumber}
                      />
                    )}
                    {columnVisibility.quantity && (
                      <DiscrepancyNumberCell
                        hasDiscrepancy={!!quantityDiscrepancy}
                        discrepancyLabel={quantityDiscrepancy?.summary}
                        fallbackTooltip={
                          hasRemainingQuantity && !row.isLumpSum
                            ? 'Remaining / Ordered'
                            : undefined
                        }
                        emphasized={isEmphasized('quantity')}
                        onDiscrepancyClick={clickHandler('quantity')}
                        onHoverChange={hoverChange('quantity')}
                      >
                        {row.isLumpSum ? (
                          formatValue(null)
                        ) : hasRemainingQuantity ? (
                          <RemainingOrderedValue
                            remaining={row.remainingQuantity}
                            ordered={row.quantity}
                            decimals={qtyDecimals}
                          />
                        ) : (
                          formatValue(row.quantity, { decimals: qtyDecimals })
                        )}
                      </DiscrepancyNumberCell>
                    )}
                    {columnVisibility.rate && (
                      <DiscrepancyNumberCell
                        hasDiscrepancy={!!rateDiscrepancy}
                        discrepancyLabel={rateDiscrepancy?.summary}
                        emphasized={isEmphasized('rate')}
                        onDiscrepancyClick={clickHandler('rate')}
                        onHoverChange={hoverChange('rate')}
                      >
                        {formatValue(
                          hidesLumpSumRate(!!row.isLumpSum, row.rate)
                            ? null
                            : row.rate,
                          { decimals: rateDecimals, prefix: '$' },
                        )}
                      </DiscrepancyNumberCell>
                    )}
                    {columnVisibility.amount && (
                      <DiscrepancyNumberCell
                        hasDiscrepancy={!!amountDiscrepancy}
                        discrepancyLabel={amountDiscrepancy?.summary}
                        fallbackTooltip={
                          hasRemainingAmount ? 'Remaining / Ordered' : undefined
                        }
                        emphasized={isEmphasized('amount')}
                        onDiscrepancyClick={clickHandler('amount')}
                        onHoverChange={hoverChange('amount')}
                      >
                        {hasRemainingAmount ? (
                          <RemainingOrderedValue
                            remaining={row.remainingAmount}
                            ordered={row.amount}
                            decimals={amountDecimals}
                            prefix="$"
                          />
                        ) : (
                          formatValue(row.amount, {
                            decimals: amountDecimals,
                            prefix: '$',
                          })
                        )}
                      </DiscrepancyNumberCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
